import dayjs from "../../configs/Dayjs.config";
import { DateRange } from "../../interfaces/DatesGlobalInterface.type";

// All patterns run on normalised text (lowercase, diacritics stripped).

const MONTHS: Readonly<Record<string, number>> = {
  // es
  enero: 0, febrero: 1, marzo: 2, abril: 3, mayo: 4, junio: 5,
  julio: 6, agosto: 7, septiembre: 8, octubre: 9, noviembre: 10, diciembre: 11,
  // en
  january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
  july: 6, august: 7, september: 8, october: 9, november: 10, december: 11,
  jan: 0, feb: 1, mar: 2, apr: 3, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
  // fr
  janvier: 0, fevrier: 1, mars: 2, avril: 3, mai: 4, juin: 5,
  juillet: 6, aout: 7, septembre: 8, octobre: 9, novembre: 10, decembre: 11,
  // it
  gennaio: 0, febbraio: 1, aprile: 3, maggio: 4, giugno: 5,
  luglio: 6, settembre: 8, ottobre: 9, dicembre: 11
};

// Longest first so "marzo" is preferred over "mar" inside alternations.
const MONTH_ALT = Object.keys(MONTHS)
  .sort((a, b) => b.length - a.length)
  .join("|");

const RE_SINCE_YESTERDAY =
  /\b(?:desde|since|from|a\s+partir\s+de)\s+(?:ayer|yesterday|ahir|hier|ieri|ontem)\b/;
const RE_TODAY = /\b(?:hoy|today|avui|aujourd.?hui|oggi|hoje|simera)\b/;
const RE_YESTERDAY = /\b(?:ayer|yesterday|ahir|hier|ieri|ontem|xtes|echtes)\b/;

// "ultimos 3 meses", "last 2 weeks", "derniers 10 jours"...
const RE_LAST_N_UNIT =
  /\b(?:ultimos?|ultimas?|last|darrers?|darreres?|derniers?|dernieres?|ultims?)\s+(\d+)\s+(dias?|days?|jours?|giorni|semanas?|weeks?|setmanes?|semaines?|settimane|meses|months?|mesos|mois|mesi)\b/;
// "hace 3 dias", "3 semanas atras", "2 months ago".
const RE_N_AGO =
  /\b(?:hace\s+(\d+)\s+(dias?|semanas?|meses)|(\d+)\s+(dias?|semanas?|meses)\s+atras|(\d+)\s+(days?|weeks?|months?)\s+ago)\b/;
// Bare "3 dias" shorthand.
const RE_N_DAYS_BARE = /\b(\d+)\s+(?:dias?|days?|jours?|giorni)\b/;

const RE_LAST_WEEK =
  /\b(?:ultima\s+semana|semana\s+(?:pasada|passada|anterior)|last\s+week|darrera\s+setmana|semaine\s+derniere|settimana\s+scorsa)\b/;
const RE_THIS_WEEK =
  /\b(?:esta\s+semana|this\s+week|aquesta\s+setmana|cette\s+semaine|questa\s+settimana)\b/;
const RE_LAST_MONTH =
  /\b(?:ultimos?\s+mes|last\s+month|darrer\s+mes|mois\s+dernier|mese\s+scorso|mes\s+(?:pasado|passado|anterior))\b/;
const RE_THIS_MONTH = /\b(?:este\s+mes|this\s+month|aquest\s+mes|ce\s+mois|questo\s+mese)\b/;
const RE_LAST_YEAR =
  /\b(?:ultimo\s+ano|last\s+year|darrer\s+any|derniere\s+annee|anno\s+scorso|ano\s+(?:pasado|passado))\b/;
const RE_THIS_YEAR = /\b(?:este\s+ano|this\s+year|aquest\s+any|quest.?anno)\b/;

const RE_SPECIFIC_RANGE = new RegExp(
  String.raw`\b(?:del?|from|du|dal|desde)\s+(?:el\s+)?(\d{1,2})\s+(?:de\s+)?(${MONTH_ALT})\b(?:\s+(?:de\s+|del\s+)?(\d{4}))?` +
    String.raw`\s+(?:al?|to|au|hasta|until)\s+(?:el\s+)?(\d{1,2})\s+(?:de\s+)?(${MONTH_ALT})\b(?:\s+(?:de\s+|del\s+)?(\d{4}))?`
);

const RE_QUARTER_Q = /\bq([1-4])\b/;
const RE_QUARTER_WORD =
  /\b(primer|segundo|tercer|cuarto|first|second|third|fourth|1st|2nd|3rd|4th)\s+(?:trimestre|quarter)\b/;
const QUARTER_WORDS: Readonly<Record<string, number>> = {
  primer: 1, first: 1, "1st": 1,
  segundo: 2, second: 2, "2nd": 2,
  tercer: 3, third: 3, "3rd": 3,
  cuarto: 4, fourth: 4, "4th": 4
};

const SEASONS: Readonly<Record<string, "spring" | "summer" | "autumn" | "winter">> = {
  primavera: "spring", spring: "spring", printemps: "spring",
  verano: "summer", summer: "summer", ete: "summer", estate: "summer", verao: "summer",
  otono: "autumn", autumn: "autumn", fall: "autumn", automne: "autumn", autunno: "autumn", outono: "autumn",
  invierno: "winter", winter: "winter", hiver: "winter", inverno: "winter"
};
const RE_SEASON = new RegExp(String.raw`\b(${Object.keys(SEASONS).join("|")})\b`);

const RE_MONTH_YEAR = new RegExp(String.raw`\b(${MONTH_ALT})\s+(?:del?\s+)?(\d{4})\b`);
const RE_IN_MONTH = new RegExp(
  String.raw`\b(?:en|durante|in|during|el\s+mes\s+de)\s+(${MONTH_ALT})\b`
);

const RE_DATE_DMY = /\b(\d{1,2})[/-](\d{1,2})[/-](\d{4})\b/;
const RE_DATE_ISO = /\b(\d{4})[/-](\d{2})[/-](\d{2})\b/;

/** Range from `start` (start of day) through end of today. */
function untilToday(start: dayjs.Dayjs, now: dayjs.Dayjs): DateRange {
  return { startDate: start.startOf("day"), endDate: now.endOf("day") };
}

function wholeDay(day: dayjs.Dayjs): DateRange {
  return { startDate: day.startOf("day"), endDate: day.endOf("day") };
}

function unitOf(token: string): "day" | "week" | "month" {
  if (/^(?:dia|day|jour|giorn)/.test(token)) { return "day"; }
  if (/^(?:seman|week|setman|semain|settiman)/.test(token)) { return "week"; }
  return "month";
}

function detectRelative(normalized: string, now: dayjs.Dayjs): DateRange | null {
  if (RE_SINCE_YESTERDAY.test(normalized)) { return untilToday(now.subtract(1, "day"), now); }
  if (RE_TODAY.test(normalized)) { return wholeDay(now); }
  if (RE_YESTERDAY.test(normalized)) { return wholeDay(now.subtract(1, "day")); }

  const lastN = RE_LAST_N_UNIT.exec(normalized);
  if (lastN) { return untilToday(now.subtract(Number(lastN[1]), unitOf(lastN[2])), now); }
  const ago = RE_N_AGO.exec(normalized);
  if (ago) {
    const n = Number(ago[1] ?? ago[3] ?? ago[5]);
    return untilToday(now.subtract(n, unitOf(ago[2] ?? ago[4] ?? ago[6])), now);
  }
  // Bounds check avoids matching ports, years, etc.
  const bare = RE_N_DAYS_BARE.exec(normalized);
  if (bare && Number(bare[1]) > 0 && Number(bare[1]) <= 365) {
    return untilToday(now.subtract(Number(bare[1]), "day"), now);
  }

  if (RE_LAST_WEEK.test(normalized)) { return untilToday(now.subtract(7, "day"), now); }
  if (RE_THIS_WEEK.test(normalized)) { return untilToday(now.startOf("week"), now); }
  if (RE_LAST_MONTH.test(normalized)) { return untilToday(now.subtract(1, "month"), now); }
  if (RE_THIS_MONTH.test(normalized)) { return untilToday(now.startOf("month"), now); }
  if (RE_LAST_YEAR.test(normalized)) { return untilToday(now.subtract(1, "year"), now); }
  if (RE_THIS_YEAR.test(normalized)) { return untilToday(now.startOf("year"), now); }
  return null;
}

/** Strict calendar date (rejects overflow like 31/02). */
function calendarDate(year: number, month: number, day: number): dayjs.Dayjs | null {
  const d = dayjs(new Date(year, month, day));
  return d.isValid() && d.year() === year && d.month() === month && d.date() === day ? d : null;
}

function detectSpecificRange(normalized: string, now: dayjs.Dayjs): DateRange | null {
  const m = RE_SPECIFIC_RANGE.exec(normalized);
  if (!m) { return null; }
  const [, sDay, sMon, sYr, eDay, eMon, eYr] = m;
  // A single year applies to both ends ("del 1 de marzo al 5 de abril de 2025").
  const startYear = Number(sYr ?? eYr ?? now.year());
  const start = calendarDate(startYear, MONTHS[sMon], Number(sDay));
  const end = calendarDate(eYr ? Number(eYr) : startYear, MONTHS[eMon], Number(eDay));
  if (!start || !end || start.isAfter(end)) { return null; }
  return { startDate: start.startOf("day"), endDate: end.endOf("day") };
}

function quarterRange(q: number, now: dayjs.Dayjs): DateRange {
  const start = now.month((q - 1) * 3).startOf("month");
  return { startDate: start, endDate: start.add(2, "month").endOf("month") };
}

function detectQuarter(normalized: string, now: dayjs.Dayjs): DateRange | null {
  const q = RE_QUARTER_Q.exec(normalized);
  if (q) { return quarterRange(Number(q[1]), now); }
  const word = RE_QUARTER_WORD.exec(normalized);
  return word ? quarterRange(QUARTER_WORDS[word[1]], now) : null;
}

/** Northern-hemisphere season for the current year. */
function detectSeason(normalized: string, now: dayjs.Dayjs): DateRange | null {
  const m = RE_SEASON.exec(normalized);
  if (!m) { return null; }
  const yr = now.year();
  const span = (from: string, to: string, toYear = yr): DateRange => ({
    startDate: dayjs(`${yr}-${from}`).startOf("day"),
    endDate: dayjs(`${toYear}-${to}`).endOf("day")
  });
  // Approximate astronomical season boundaries (MM-DD).
  switch (SEASONS[m[1]]) {
    case "spring": return span("03-20", "06-20");
    case "summer": return span("06-21", "09-22");
    case "autumn": return span("09-23", "12-20");
    default: return span("12-21", "03-19", yr + 1);
  }
}

function detectMonth(normalized: string, now: dayjs.Dayjs): DateRange | null {
  const withYear = RE_MONTH_YEAR.exec(normalized);
  if (withYear) {
    const start = now.year(Number(withYear[2])).month(MONTHS[withYear[1]]).startOf("month");
    return { startDate: start, endDate: start.endOf("month") };
  }
  const inMonth = RE_IN_MONTH.exec(normalized);
  if (inMonth) {
    const idx = MONTHS[inMonth[1]];
    // A month later than the current one refers to last year.
    const start = now.year(idx > now.month() ? now.year() - 1 : now.year()).month(idx).startOf("month");
    return { startDate: start, endDate: start.endOf("month") };
  }
  return null;
}

function detectNumericDate(normalized: string): DateRange | null {
  const dmy = RE_DATE_DMY.exec(normalized);
  const dmyDate = dmy && calendarDate(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
  if (dmyDate) { return wholeDay(dmyDate); }
  const iso = RE_DATE_ISO.exec(normalized);
  const isoDate = iso && calendarDate(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  return isoDate ? wholeDay(isoDate) : null;
}

/**
 * Parses supported natural-language date expressions from normalised text.
 * Returns null when none are recognized.
 */
export function detectDatePeriod(normalized: string, now: dayjs.Dayjs = dayjs()): DateRange | null {
  return (
    detectSpecificRange(normalized, now) ??
    detectRelative(normalized, now) ??
    detectQuarter(normalized, now) ??
    detectSeason(normalized, now) ??
    detectMonth(normalized, now) ??
    detectNumericDate(normalized)
  );
}

/** DateRange spanning today. */
export function todayDateRange(now: dayjs.Dayjs = dayjs()): DateRange {
  return wholeDay(now);
}

/** Month names recognised by the date parser (used to skip them as context terms). */
export const CHATBOT_MONTH_NAMES: ReadonlySet<string> = new Set(Object.keys(MONTHS));
